import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  fromPromise,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import {
  getMainDefinition,
  relayStylePagination,
} from '@apollo/client/utilities';
import { Auth0Client } from '@auth0/auth0-spa-js';
import { createClient } from 'graphql-ws';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { config } from './config';

const authClient = new Auth0Client(config.auth0);

const getAuthorization = async () => {
  const getAccessTokenSilently =
    (window as any).getAccessTokenSilently ?? authClient.getTokenSilently;
  try {
    const accessToken = await getAccessTokenSilently();
    if (accessToken) {
      return `Bearer ${accessToken}`;
    }
  } catch (err) {
    // noop
  }
  return undefined;
};

// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/47369
const httpLink = createUploadLink({
  uri: config.apiUrl,
  headers: {
    'Apollo-Require-Preflight': 'true',
  },
}) as unknown as ApolloLink;

const errorLink = onError(
  ({ graphQLErrors = [], networkError, forward, operation }) => {
    const errorCodes = graphQLErrors.map((err) => err.extensions?.code);
    const errorMessage = graphQLErrors.map((err) => err.message);

    // Test for authentication errors
    if (
      errorCodes.includes('UNAUTHENTICATED') ||
      errorMessage.some((message) => message.startsWith('Access denied!'))
    ) {
      // Unauthorized. You need to sign in.
      return fromPromise(getAuthorization())
        .filter((tokens) => !!tokens)
        .flatMap((accessToken) => {
          operation.setContext({
            headers: {
              ...operation.getContext().headers,
              authorization: accessToken,
            },
          });
          return forward(operation);
        });
    }

    console.log({ graphQLErrors, networkError });
  }
);

const authLink = setContext(async (req, { headers }) => ({
  headers: {
    ...headers,
    authorization: await getAuthorization(),
  },
}));

export const ws = {
  connected: false,
};
const wsClient = createClient({
  url: config.apiUrl.replace(/^http/, 'ws'),
  connectionParams: async () => {
    const Authorization = await getAuthorization();
    if (!Authorization) {
      return {};
    }
    return {
      Authorization,
    };
  },
  on: {
    connected: () => {
      ws.connected = true;
    },
    closed: () => {
      ws.connected = false;
    },
  },
});

const wsLink = new GraphQLWsLink(wsClient);

const httpSothebysLink = createHttpLink({
  uri: config.clientApiUrl,
});

const splitHttpLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.name?.value.startsWith('sothebys') ?? false;
  },
  httpSothebysLink,
  httpLink
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  splitHttpLink
);

const link = ApolloLink.from([errorLink, authLink, splitLink]);

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        allLots: relayStylePagination(),
      },
    },
  },
});

export const client = new ApolloClient({
  link,
  cache,
  name: 'web',
  version: '1.0.0',
});
